// SalesCRM / index.js — All Leads grid with Add Lead modal.
import React, { Component } from "react";
import { Link } from "react-router-dom";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Popper from "@material-ui/core/Popper";
import HelperClass from "../../../config/helperClass";
import { THEME_COLOR, THEME_TEXT_COLOR } from "../../../config/config";
import dateFormat from "dateformat";
import Select from "react-select";
import ReactPhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/material.css";
import GooglePlacesAutocomplete from "react-google-places-autocomplete";
import BulkImportLeadsModal from "./BulkImportLeadsModal";
import {
  listLeadsGrid,
  listStages,
  getPipelineKpis,
  getFunnelMetrics,
  createLead,
  listPartners,
  listPartnerProducts,
  getScoreWeights,
} from "./api/leads_api";
import { FALLBACK_STAGES, TABS, DEFAULT_PAGE_SIZE, PRIORITIES, LEAD_SOURCES } from "./api/leads_constants";

const auth = new HelperClass();

const BottomPopper = (props) => (
  <Popper {...props} placement="bottom-start" modifiers={{ flip: { enabled: false } }} />
);
var SCORE_BANDS = [
  { min: 70, max: 100, label: "High", color: "#1a7a4a", bg: "#e6f4ec" },
  { min: 40, max: 69,  label: "Medium", color: "#d97706", bg: "#fef3c7" },
  { min: 0,  max: 39,  label: "Low",  color: "#6b7280", bg: "#f3f4f6" },
];
function fmtMoney(n) {
  const v = Number(n) || 0;
  return "$" + v.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function StagePill({ stage_key, stages }) {
  const s = (stages || []).find((x) => x.stage_key === stage_key) || { label: stage_key, color_hex: "#444", bg_hex: "#eee" };
  return (
    <span
      title={s.label}
      style={{
        display: "inline-block", padding: "3px 10px", borderRadius: 12,
        fontSize: 12, fontWeight: 600, color: s.color_hex, background: s.bg_hex,
        maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        verticalAlign: "middle",
      }}
    >{s.label}</span>
  );
}

function PriorityPill({ priority }) {
  const m = { high: { c: "#B02A2A", b: "#FEE2E2", l: "High" },
              med:  { c: "#C25A00", b: "#FDF0E5", l: "Medium" },
              low:  { c: "#1A7A4A", b: "#E6F4EC", l: "Low" } };
  const s = m[priority] || m.med;
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 10,
      fontSize: 11, fontWeight: 600, color: s.c, background: s.b,
    }}>{s.l}</span>
  );
}

function ageLabel(created_at) {
  if (!created_at) return null;
  var d = new Date(created_at);
  if (isNaN(d.getTime())) return null;
  var diff = Date.now() - d.getTime();
  if (diff < 0) return null;
  var mins = Math.floor(diff / 60000);
  if (mins < 60) return (mins < 1 ? "< 1m" : mins + "m");
  var hrs = Math.floor(diff / 3600000);
  if (hrs < 24) return hrs + "h";
  return Math.floor(diff / 86400000) + "d";
}

function AgeBadge({ created_at }) {
  var label = ageLabel(created_at);
  if (!label) return null;
  var days = Math.floor((Date.now() - new Date(created_at).getTime()) / 86400000);
  var color = days > 30 ? "#B02A2A" : days > 7 ? "#C25A00" : "#1A7A4A";
  var bg    = days > 30 ? "#FEE2E2" : days > 7 ? "#FDF0E5" : "#E6F4EC";
  return (
    <span style={{
      display: "inline-flex", alignItems: "center",
      padding: "1px 6px", borderRadius: 9999,
      background: bg, fontSize: 10.5, fontWeight: 700, color: color,
      whiteSpace: "nowrap", letterSpacing: "0.01em",
    }}>{label}</span>
  );
}

export default class AdminLeads extends Component {
  constructor(props) {
    super(props);
    const view = props && props.props && props.props.view;
    let initialTab = "all";
    if (view === "crm_leads_new") initialTab = "new";
    else if (view === "crm_leads_won") initialTab = "won";
    this.state = {
      loading: true,
      stages: FALLBACK_STAGES,
      rows: [],
      total: 0,
      page: 1,
      page_size: DEFAULT_PAGE_SIZE,
      tab: initialTab,
      search: "",
      kpis: null,
      funnel: [],
      scoreWeights: null,
      errorMessage: "",
      // filters
      stage_filter: "",
      partner_filter: "",
      priority_filter: "",
      // add lead modal
      showAdd: false,
      adding: false,
      addErr: "",
      partnersLoading: false,
      partners: [],
      partnerProducts: [],
      form: {
        company: "", contact_name: "", email: "", phone: "",
        job_title: "", industry: "", website: "", address: "",
        city: "", state: "", zip: "", country: "",
        deal_value: "", priority: "med", stage_key: "new",
        source: "", score: 50, next_action_date: "",
        partner_accountno: "", notes: "", products: [],
      },
      // bulk import modal
      showBulkImport: false,
    };
  }

  componentDidMount() {
    this.loadStages();
    this.loadGrid();
    this.loadKpis();
    this.loadFunnel();
    this.loadScoreWeights();
    this.loadPartners();
    this._onKeyDown = (e) => {
      if (e.key === "Escape" && this.state.showAdd && !this.state.adding) this.closeAddModal();
    };
    document.addEventListener("keydown", this._onKeyDown);
  }

  componentWillUnmount() {
    if (this._onKeyDown) document.removeEventListener("keydown", this._onKeyDown);
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }

  loadStages = async () => {
    try {
      const r = await listStages(auth.getAccount(), auth.getToken());
      if (r && r.data && r.data.status === 200 && Array.isArray(r.data.data) && r.data.data.length > 0) {
        this.setState({ stages: r.data.data });
      }
    } catch (_) {}
  };

  loadScoreWeights = async () => {
    try {
      const r = await getScoreWeights(auth.getAccount(), auth.getToken());
      if (r && r.data && r.data.status === 200 && r.data.data) {
        this.setState({ scoreWeights: r.data.data });
      }
    } catch (_) {}
  };

  loadPartners = async () => {
    try {
      const r = await listPartners(auth.getAccount(), auth.getToken());
      if (r && r.data && r.data.status === 200) {
        this.setState({ partners: r.data.data || [] });
      }
    } catch (_) {}
  };

  loadKpis = async () => {
    try {
      const r = await getPipelineKpis(auth.getAccount(), auth.getToken(), {});
      if (r && r.data && r.data.status === 200) {
        this.setState({ kpis: r.data.data });
      }
    } catch (_) {}
  };

  loadFunnel = async () => {
    try {
      const r = await getFunnelMetrics(auth.getAccount(), auth.getToken(), {});
      if (r && r.data && r.data.status === 200) {
        this.setState({ funnel: r.data.data || [] });
      }
    } catch (_) {}
  };

  loadGrid = async () => {
    this.setState({ loading: true, errorMessage: "" });
    const { tab, page, page_size, search, stage_filter, partner_filter, priority_filter } = this.state;
    // Special tabs that the backend understands
    const specialTabs = ["all", "new_leads", "won", "lost", "in_progress"];
    const isSpecialTab = specialTabs.includes(tab);
    const filters = {
      tab: isSpecialTab ? tab : undefined,
      page,
      page_size,
      search: search || undefined,
      stage_key: !isSpecialTab ? tab : (stage_filter || undefined),
      partner_accountno: partner_filter || undefined,
      priority: priority_filter || undefined,
      sort_by: "created_at",
      sort_dir: "DESC",
    };
    try {
      const r = await listLeadsGrid(auth.getAccount(), auth.getToken(), filters);
      if (r && r.data && r.data.status === 200 && r.data.data) {
        this.setState({ rows: r.data.data.rows || [], total: r.data.data.total || 0, loading: false });
      } else {
        this.setState({
          loading: false,
          errorMessage: (r && r.data && (r.data.message || (r.data.errors && r.data.errors.message))) || "Failed to load leads",
        });
      }
    } catch (e) {
      this.setState({ loading: false, errorMessage: String(e && e.message ? e.message : e) });
    }
  };

  switchTab = (key) => { this.setState({ tab: key, page: 1 }, this.loadGrid); };
  onSearchChange = (e) => {
    const value = e.target.value;
    this.setState({ search: value });
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.setState({ page: 1 }, this.loadGrid);
    }, 400);
  };
  onSearchSubmit = (e) => {
    if (e.key === "Enter") {
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this.setState({ page: 1 }, this.loadGrid);
    }
  };
  onSearchClear = () => {
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this.setState({ search: "", page: 1 }, this.loadGrid);
  };
  onStageFilterChange = (e) => this.setState({ stage_filter: e.target.value, page: 1 }, this.loadGrid);
  onPartnerFilterChange = (e) => this.setState({ partner_filter: e.target.value, page: 1 }, this.loadGrid);
  onPriorityFilterChange = (e) => this.setState({ priority_filter: e.target.value, page: 1 }, this.loadGrid);
  changePage = (delta) => {
    const { page, total, page_size } = this.state;
    const maxPage = Math.max(1, Math.ceil(total / page_size));
    const next = Math.max(1, Math.min(maxPage, page + delta));
    if (next === page) return;
    this.setState({ page: next }, this.loadGrid);
  };

  openLead = (lead_uid) => {
    this.props.props.history.push("/crm_lead_detail/" + lead_uid);
  };

  exportCsv = () => {
    const { rows, partners } = this.state;
    if (!rows || rows.length === 0) return;
    const headers = ["Lead UID", "Company Name", "Inquiry Type", "Industry", "Address", "Contact Name", "Email", "Stage", "Deal Value", "Partner", "Next Action", "Score"];
    const escape = (v) => {
      const s = v == null ? "" : String(v);
      return s.includes(",") || s.includes("\"") || s.includes("\n") ? "\"" + s.replace(/"/g, "\"\"") + "\"" : s;
    };
    const csvRows = [headers.join(",")].concat(
      rows.map((r) => {
        var p = partners.find(function(p) { return p.accountno === r.partner_accountno; });
        var partnerLabel = p ? (p.comp_name || p.name) : (r.partner_name || r.partner_contact || r.partner_accountno || "");
        return [
          r.lead_uid,
          r.company,
          r.inquiry_type || '',
          r.industry || '',
          r.address || '',
          r.contact_name || '',
          r.email || '',
          r.stage_key,
          r.deal_value,
          partnerLabel,
          r.next_action_date || '',
          r.score,
        ].map(escape).join(",");
      })
    );
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "leads_export_" + new Date().toISOString().slice(0, 10) + ".csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  format_date_only = (value) => {
    let date = value;
    if (date && date !== "") {
      return dateFormat(date, "mm-dd-yyyy");
    } else {
      return "---";
    }
  };

  // ---- Add Lead modal ----
  openAddModal = async () => {
    this.setState({ showAdd: true, addErr: "", adding: false, partnersLoading: true });
    try {
      const r = await listPartners(auth.getAccount(), auth.getToken());
      if (r && r.data && r.data.status === 200) {
        this.setState({ partners: r.data.data || [], partnersLoading: false });
      } else {
        const msg = (r && r.data && (r.data.message || (r.data.errors && r.data.errors.message))) || "Could not load partner list.";
        this.setState({ partnersLoading: false, addErr: msg });
      }
    } catch (e) {
      this.setState({ partnersLoading: false, addErr: "Could not load partners: " + String(e && e.message ? e.message : e) });
    }
  };

  closeAddModal = () => this.setState({
    showAdd: false, addErr: "", partnersLoading: false,
    form: {
      company: "", contact_name: "", email: "", phone: "",
      job_title: "", industry: "", website: "", address: "",
      city: "", state: "", zip: "", country: "",
      deal_value: "", priority: "med", stage_key: "new",
      source: "", score: 50, next_action_date: "",
      partner_accountno: "", notes: "", products: [],
    },
  });

  // ---- Bulk Import modal ----
  openBulkImportModal = () => {
    this.setState({ showBulkImport: true });
  };

  closeBulkImportModal = () => {
    this.setState({ showBulkImport: false });
  };

  onBulkImportSuccess = () => {
    this.loadGrid();
    this.loadKpis();
    this.loadFunnel();
  };

  patchForm = (key, val) => this.setState((s) => ({ form: { ...s.form, [key]: val } }));

  onAddressSelect = async (place) => {
    const description = place && place.value && place.value.description ? place.value.description : "";
    // Parse city/state/zip/country from description if possible
    // Format is typically: "Street, City, State ZIP, Country"
    const parts = description.split(",").map(function(p) { return p.trim(); });
    var city = ""; var state = ""; var zip = ""; var country = "";
    if (parts.length >= 3) {
      city = parts[parts.length - 3] || "";
      var stateZip = parts[parts.length - 2] || "";
      var spIdx = stateZip.indexOf(" ");
      if (spIdx !== -1) {
        state = stateZip.substring(0, spIdx);
        zip = stateZip.substring(spIdx + 1);
      } else {
        state = stateZip;
      }
      country = parts[parts.length - 1] || "";
    }
    this.setState(function(s) {
      return {
        form: Object.assign({}, s.form, {
          address: description,
          city: city,
          state: state,
          zip: zip,
          country: country,
        })
      };
    });
  };

  onPartnerChange = async (partner_accountno) => {
    this.patchForm("partner_accountno", partner_accountno);
    this.patchForm("products", []);
    this.patchForm("deal_value", "");
    if (!partner_accountno) { this.setState({ partnerProducts: [] }); return; }
    try {
      const r = await listPartnerProducts(auth.getAccount(), auth.getToken(), partner_accountno);
      if (r && r.data && r.data.status === 200) {
        this.setState({ partnerProducts: r.data.data || [] });
      }
    } catch (_) {}
  };

  recalcDealValue = (products) => {
    const total = (products || []).reduce((sum, p) => sum + (parseFloat(p.rate) || 0), 0);
    return total > 0 ? total.toFixed(2) : "";
  };

  addProduct = (svc) => {
    this.setState((s) => {
      const products = [
        ...(s.form.products || []),
        { service_id: svc.service_id, service_title: svc.service_title, rate: svc.rate },
      ];
      return { form: { ...s.form, products, deal_value: this.recalcDealValue(products) } };
    });
  };

  removeProductAt = (index) => {
    this.setState((s) => {
      const products = (s.form.products || []).filter((_, i) => i !== index);
      return { form: { ...s.form, products, deal_value: this.recalcDealValue(products) } };
    });
  };

  submitAddLead = async () => {
    const { form } = this.state;
    if (!form.company.trim()) { this.setState({ addErr: "Company name is required." }); return; }
    this.setState({ adding: true, addErr: "" });
    const payload = Object.assign({}, form, {
      products: JSON.stringify(form.products || []),
    });
    try {
      const r = await createLead(auth.getAccount(), auth.getToken(), payload);
      if (r && r.data && r.data.status === 200) {
        this.closeAddModal();
        this.loadGrid();
        this.loadKpis();
      } else {
        const msg = (r && r.data && (r.data.message || (r.data.errors && r.data.errors.message))) || "Failed to create lead.";
        this.setState({ adding: false, addErr: msg });
      }
    } catch (e) {
      this.setState({ adding: false, addErr: String(e && e.message ? e.message : e) });
    }
  };

  render() {
    const { loading, rows, total, page, page_size, tab, stages, kpis, funnel, scoreWeights,
            errorMessage, search, showAdd, adding, addErr,
            partners, partnerProducts, partnersLoading, form,
            stage_filter, partner_filter, priority_filter } = this.state;
    const sw = scoreWeights || { engagement: 30, deal_value: 25, stage_progression: 20, activity_recency: 15, priority: 10 };
    const maxPage = Math.max(1, Math.ceil(total / page_size));
    const dash = "\u2014";
    const gridWrapBorderStyle = { borderTop: "1px solid #e5e9f2" };
    const gridStyle = { minWidth: 1280, tableLayout: "fixed", marginBottom: 0 };
    const thStyle = { padding: "16px 16px", color: "#374151", fontSize: 14, fontWeight: 500, letterSpacing: "0", textTransform: "none", verticalAlign: "middle", whiteSpace: "nowrap", borderBottom: "2px solid #e5e9f2", backgroundColor: "#fff" };
    const tdStyle = { padding: "14px 16px", verticalAlign: "middle", color: "#364a63", fontSize: 14, lineHeight: 1.5, borderBottom: "1px solid #f0f2f4" };
    const mutedStyle = { fontSize: 12, color: "#8091a7" };
    const clampStyle = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
    const wrapTextStyle = { whiteSpace: "normal", overflowWrap: "anywhere" };
    const activeTotal = (funnel || []).filter(function(r) { return !(r.stage && r.stage.is_won) && !(r.stage && r.stage.is_lost); }).reduce(function(a, r) { return { count: a.count + (Number(r.count) || 0), value: a.value + (Number(r.value) || 0) }; }, { count: 0, value: 0 });
    const wonTotal = (funnel || []).filter(function(r) { return r.stage && r.stage.is_won; }).reduce(function(a, r) { return { count: a.count + (Number(r.count) || 0), value: a.value + (Number(r.value) || 0) }; }, { count: 0, value: 0 });
    const lostTotal = (funnel || []).filter(function(r) { return r.stage && r.stage.is_lost; }).reduce(function(a, r) { return { count: a.count + (Number(r.count) || 0), value: a.value + (Number(r.value) || 0) }; }, { count: 0, value: 0 });
    return (
      <div className="nk-content">
        <div className="container-fluid">
          <div className="nk-content-inner">
            <div className="nk-content-body">
              <div className="nk-block-head nk-block-head-sm">
                <div className="nk-block-between flex-wrap g-3">
                  <div className="nk-block-head-content">
                    <h3 className="nk-block-title page-title">
                      {tab === "all" ? "All Leads" : (stages.find(function(s) { return s.stage_key === tab; }) || {}).label || "Leads"}
                    </h3>
                    <div className="nk-block-des text-soft"><p>Detailed lead register — click any row for full detail</p></div>
                  </div>
                  <div className="nk-block-head-content" id="HeadContentLeads">
                    <div className="toggle-wrap nk-block-tools-toggle">
                      <a href="#" className="btn btn-icon btn-trigger toggle-expand mr-n1" data-target="leadsMenu">
                        <em className="icon ni ni-more-v"></em>
                      </a>
                      <div className="toggle-expand-content" data-content="leadsMenu">
                        <ul className="nk-block-tools g-3">
                          <li className="nk-block-tools-opt">
                            <button type="button" className="btn btn-info" style={{ color: "#fff" }} onClick={this.exportCsv} title="Export CSV">
                              <em className="icon ni ni-download"></em>&nbsp;Export CSV
                            </button>
                          </li>
                          <li className="nk-block-tools-opt">
                            <Link to="/crm_leads_kanban" className="btn btn-info" style={{ color: "#fff" }}>
                              <em className="icon ni ni-layout"></em>&nbsp;Kanban View
                            </Link>
                          </li>
                          <li className="nk-block-tools-opt">
                            <button type="button" className="btn btn-info" style={{ color: "#fff" }} onClick={this.openBulkImportModal} title="Bulk Import Leads from CSV/Excel">
                              <em className="icon ni ni-import"></em>&nbsp;Bulk Import
                            </button>
                          </li>
                          <li className="nk-block-tools-opt">
                            <button type="button" className="btn btn-primary" style={{ color: "#fff" }} onClick={this.openAddModal}>
                              <em className="icon ni ni-plus"></em>&nbsp;Add Lead
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {kpis ? (
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
                  {[
                    { label: "All Leads",       value: String(kpis.total_leads || 0),          color: "#2563eb", bg: "#e8eefb", icon: "ni ni-users", suffix: fmtMoney(kpis.pipeline_value) + " total value" },
                    { label: "Open Pipeline",   value: String(activeTotal.count || 0),         color: "#2563eb", bg: "#e8eefb", icon: "ni ni-plus-circle", suffix: fmtMoney(activeTotal.value || kpis.pipeline_value), accentSub: true },
                    { label: "Won",             value: String(wonTotal.count || kpis.won_qtd_count || 0), color: "#198754", bg: "#e6f4ec", icon: "ni ni-check", suffix: fmtMoney(wonTotal.value || kpis.won_qtd_value), accentSub: true },
                    // { label: "Lost",            value: String(lostTotal.count || 0),           color: "#dc3545", bg: "#f8e8e8", icon: "ni ni-cross", suffix: fmtMoney(lostTotal.value), accentSub: true },
                    { label: "Avg Deal Size",   value: fmtMoney(kpis.avg_deal_size),           color: "#d97706", bg: "#fef3c7", icon: "ni ni-sign-dollar", suffix: "Across " + (kpis.total_leads || 0) + " leads" },
                  ].map((c, i) => (
                    <div key={i} style={{ display: "contents" }}>
                      <div className="card card-bordered" style={{ flex: "1 1 190px", minWidth: 180, minHeight: 84, borderRadius: 8 }}>
                        <div className="card-inner" style={{ minHeight: 82, padding: "14px 16px", position: "relative" }}>
                          <div style={{ display: "block", marginBottom: 4, paddingLeft: 48 }}>
                            <div style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", color: "#6f6a64" }}>{c.label}</div>
                            <div style={{ width: 36, height: 36, borderRadius: 8, background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", color: c.color, position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)" }}>
                              <em className={"icon " + c.icon} style={{ fontSize: 18 }}></em>
                            </div>
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: "#111827", lineHeight: 1.05, marginLeft: 48 }}>
                            {c.value}
                          </div>
                          {c.suffix ? <div style={{ fontSize: 11, color: c.accentSub ? c.color : "#7a746d", marginTop: 4, marginLeft: 48, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.suffix}</div> : null}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* ── Lead Score Formula ── */}
              <div className="example-alert" style={{ marginBottom: 20 }}>
                <div className="alert alert-pro alert-info">
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                      <em className="icon ni ni-info text-info" style={{ fontSize: 14, lineHeight: 1 }}></em>
                      <strong style={{ fontSize: 14 }}>Lead Score Formula (0 – 100)</strong>
                    </div>
                    Composite of: <strong>Engagement (30%) · Deal value vs. avg (25%) · Stage progression (20%) · Activity recency (15%) · Priority (10%).</strong>
                    <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <span className="badge badge-pill badge-outline-success">High 70–100</span>
                      <span className="badge badge-pill badge-outline-warning">Medium 40–69</span>
                      <span className="badge badge-pill badge-outline-secondary">Low 0–39</span>
                      <span style={{ fontSize: 12 }}>· Edit weights in&nbsp;<Link to="/crm_leads_settings">Pipeline Settings</Link>&nbsp;form.</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card card-bordered">
                <div className="card-inner border-bottom">
                  <div className="row g-2 align-items-center">

                      <div className="col-auto">
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          <label style={{ fontSize: 12, fontWeight: 600, color: "#526484", whiteSpace: "nowrap", marginBottom: 0 }}>View:</label>
                          <select
                            className="form-control form-control-sm"
                            value={tab}
                            onChange={(e) => {
                              var v = e.target.value;
                              if (v === "__archived__") {
                                this.props.props.history.push("/crm_leads_archived");
                              } else {
                                this.switchTab(v);
                              }
                            }}
                            style={{ minWidth: 140, fontSize: 13 }}
                          >
                            <option value="all">All Leads</option>
                            {stages.filter(function(s) { return s.active !== 0; }).map(function(s) {
                              return <option key={s.stage_key} value={s.stage_key}>{s.label}</option>;
                            })}
                            <option value="__archived__">Archived Leads</option>
                          </select>
                        </div>
                      </div>
                      <div className="col-12 col-sm col-md" style={{ position: "relative" }}>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Search company, contact, email…"
                          value={search}
                          onChange={this.onSearchChange}
                          onKeyDown={this.onSearchSubmit}
                          style={{ width: "100%", paddingRight: search ? 28 : 10 }}
                        />
                        {search && (
                          <button
                            type="button"
                            onClick={this.onSearchClear}
                            title="Clear search"
                            style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, color: "#9ca3af", fontSize: 14 }}
                          >
                            <em className="icon ni ni-cross"></em>
                          </button>
                        )}
                      </div>
                      <div className="col-12 col-sm-auto" style={{ minWidth: 130 }}>
                        <Select
                          isClearable
                          placeholder="All Stages"
                          value={stage_filter ? { value: stage_filter, label: stages.find(s => s.stage_key === stage_filter)?.label || stage_filter } : null}
                          onChange={(option) => this.onStageFilterChange({ target: { value: option?.value || "" } })}
                          options={stages.filter((s) => s.active).map((s) => ({ value: s.stage_key, label: s.label }))}
                          styles={{
                            control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 13 }),
                            indicatorsContainer: (base) => ({ ...base, height: 32 }),
                            valueContainer: (base) => ({ ...base, height: 32, padding: '0 8px' })
                          }}
                        />
                      </div>
                      <div className="col-12 col-sm-auto" style={{ minWidth: 130 }}>
                        <Select
                          isClearable
                          placeholder="All Partners"
                          value={partner_filter ? { value: partner_filter, label: partners.find(p => p.accountno === partner_filter)?.comp_name || partners.find(p => p.accountno === partner_filter)?.name || partner_filter } : null}
                          onChange={(option) => this.onPartnerFilterChange({ target: { value: option?.value || "" } })}
                          options={partners.map((p) => ({ value: p.accountno, label: p.comp_name || p.name }))}
                          styles={{
                            control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 13 }),
                            indicatorsContainer: (base) => ({ ...base, height: 32 }),
                            valueContainer: (base) => ({ ...base, height: 32, padding: '0 8px' })
                          }}
                        />
                      </div>
                      <div className="col-12 col-sm-auto" style={{ minWidth: 120 }}>
                        <Select
                          isClearable
                          placeholder="All Priority"
                          value={priority_filter ? { value: priority_filter, label: PRIORITIES.find(p => p.value === priority_filter)?.label || priority_filter } : null}
                          onChange={(option) => this.onPriorityFilterChange({ target: { value: option?.value || "" } })}
                          options={PRIORITIES.map((p) => ({ value: p.value, label: p.label }))}
                          styles={{
                            control: (base) => ({ ...base, minHeight: 32, height: 32, fontSize: 13 }),
                            indicatorsContainer: (base) => ({ ...base, height: 32 }),
                            valueContainer: (base) => ({ ...base, height: 32, padding: '0 8px' })
                          }}
                        />
                      </div>
                    </div>
                  </div>{/* end card-inner border-bottom (filters) */}

                  {errorMessage ? (
                    <div className="card-inner">
                      <div className="example-alert">
                        <div className="alert alert-pro alert-danger">
                          <div className="alert-text"><h6>Error</h6><p>{errorMessage}</p></div>
                        </div>
                      </div>
                    </div>
                  ) : null}

                  <div className="card-inner p-0" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                    <table className="table" style={gridStyle}>
                      <colgroup>
                        <col style={{ width: 110 }} />
                        <col style={{ width: 150 }} />
                        <col style={{ width: 130 }} />
                        <col style={{ width: 120 }} />
                        <col style={{ width: 190 }} />
                        <col style={{ width: 220 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 90 }} />
                        <col style={{ width: 140 }} />
                        <col style={{ width: 115 }} />
                        <col style={{ width: 75 }} />
                        <col style={{ width: 75 }} />
                      </colgroup>
                      <thead className="tb-tnx-head">
                        <tr className="tb-tnx-item">
                          <th className="tb-tnx-info" style={thStyle}>Lead</th>
                          <th className="tb-tnx-info" style={thStyle}>Company Name</th>
                          <th className="tb-tnx-info" style={thStyle}>Contact</th>
                          <th className="tb-tnx-info" style={thStyle}>Inquiry Type</th>
                          <th className="tb-tnx-info" style={thStyle}>Industry</th>
                          <th className="tb-tnx-info" style={thStyle}>Address</th>
                          <th className="tb-tnx-info" style={thStyle}>Stage</th>
                          <th className="tb-tnx-info" style={thStyle}>Value</th>
                          <th className="tb-tnx-info" style={thStyle}>Partner</th>
                          <th className="tb-tnx-info" style={thStyle}>Next Action</th>
                          <th className="tb-tnx-info" style={{ ...thStyle, textAlign: "center" }}>Score</th>
                          <th className="tb-tnx-info" style={{ ...thStyle, textAlign: "center" }}>Priority</th>
                          <th className="tb-tnx-info" style={{ ...thStyle, textAlign: "right" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr><td colSpan={13} style={{ textAlign: "center", padding: 24 }}><div className="spinner-border spinner-border-sm text-primary" role="status"></div></td></tr>
                        ) : rows.length === 0 ? (
                          <tr><td colSpan={13} style={{ textAlign: "center", padding: 24 }} className="text-soft">No leads found.</td></tr>
                        ) : rows.map((r) => {
                          const partnerObj = partners.find(function(p) { return p.accountno === r.partner_accountno; });
                          const partnerLabel = partnerObj ? (partnerObj.comp_name || partnerObj.name) : (r.partner_name || r.partner_contact || "");
                          return (
                          <tr key={r.id} className="tb-tnx-item" style={{ cursor: "pointer" }} onClick={() => this.openLead(r.lead_uid)}>
                            <td className="tb-tnx-info" style={tdStyle}>
                              <div
                                title={r.lead_uid || ""}
                                style={{ color: "#1c5bde", ...clampStyle }}
                              >
                                {r.lead_uid || dash}
                              </div>
                              <div style={{ marginTop: 6 }}><AgeBadge created_at={r.created_at} /></div>
                            </td>
                            <td className="tb-tnx-info" style={tdStyle}>
                              <div title={r.company || ""} style={{ color: "#253858", ...clampStyle }}>{r.company || dash}</div>
                              {r.source ? <div title={r.source} style={{ ...mutedStyle, marginTop: 5, ...clampStyle }}>{r.source}</div> : null}
                            </td>
                            <td className="tb-tnx-info" style={tdStyle}>
                              <div title={r.contact_name || ""} style={{ fontWeight: 600, ...clampStyle }}>{r.contact_name || dash}</div>
                              <div title={r.email || ""} style={{ ...mutedStyle, ...clampStyle }}>{r.email || ""}</div>
                            </td>
                            <td className="tb-tnx-info" title={r.inquiry_type || ""} style={{ ...tdStyle, ...clampStyle }}>{r.inquiry_type || dash}</td>
                            <td className="tb-tnx-info" title={r.industry || ""} style={{ ...tdStyle, ...clampStyle }}>{r.industry || dash}</td>
                            <td className="tb-tnx-info" style={{ ...tdStyle, ...wrapTextStyle }}>{r.address || dash}</td>
                            <td className="tb-tnx-info" style={{ ...tdStyle, overflow: "hidden" }}><StagePill stage_key={r.stage_key} stages={stages} /></td>
                            <td className="tb-tnx-info" style={tdStyle}>{fmtMoney(r.deal_value)}</td>
                            <td className="tb-tnx-info" title={partnerLabel} style={{ ...tdStyle, ...clampStyle }}>
                              {partnerLabel || dash}
                            </td>
                            <td className="tb-tnx-info" style={tdStyle}>{this.format_date_only(r.next_action_date)}</td>
                            <td className="tb-tnx-info" style={{ ...tdStyle, textAlign: "center" }}>
                              {r.score != null ? (
                                <span style={{
                                  display: "inline-block", padding: "2px 8px", borderRadius: 10,
                                  fontSize: 12, fontWeight: 700,
                                  color: r.score >= 80 ? "#1A7A4A" : r.score >= 60 ? "#C25A00" : "#B02A2A",
                                  background: r.score >= 80 ? "#E6F4EC" : r.score >= 60 ? "#FDF0E5" : "#FEE2E2",
                                }}>{r.score}</span>
                              ) : "—"}
                            </td>
                            <td className="tb-tnx-info" style={{ ...tdStyle, textAlign: "center" }}>
                              {r.priority ? <PriorityPill priority={r.priority} /> : "—"}
                            </td>
                            <td className="tb-tnx-info" style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                              <a
                                title="View Lead"
                                onClick={(e) => { e.stopPropagation(); this.openLead(r.lead_uid); }}
                                style={{ cursor: "pointer" }}
                              >
                                <em className="icon ni ni-eye text-info" style={{ fontSize: 20 }}></em>
                              </a>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="card-inner" style={{ background: "#f0f2f4" }}>
                    <div className="nk-block-between-md g-3">
                      <div className="g">
                        <span style={{ color: "#526484", fontSize: 13 }}>
                          Showing {rows.length === 0 ? 0 : (page - 1) * page_size + 1}–{(page - 1) * page_size + rows.length} of {total}
                        </span>
                      </div>
                      <div className="g">
                        <ul className="pagination justify-content-center justify-content-md-end">
                          <li className={"page-item " + (page <= 1 ? "disabled" : "")}>
                            <button type="button" className="page-link" onClick={() => this.changePage(-1)}>Prev</button>
                          </li>
                          <li className="page-item active"><span className="page-link">{page} / {maxPage}</span></li>
                          <li className={"page-item " + (page >= maxPage ? "disabled" : "")}>
                            <button type="button" className="page-link" onClick={() => this.changePage(1)}>Next</button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>
              </div>
            </div>
          </div>
        </div>

        {/* ====== Add Lead Modal ====== */}
        {showAdd && (
          <div
            className="modal fade show"
            style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
            tabIndex="-1"
            onClick={this.closeAddModal}
          >
            <div className="modal-dialog modal-lg modal-dialog-scrollable" role="document" onClick={function(e) { e.stopPropagation(); }}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add New Lead</h5>
                  <button type="button" className="close" onClick={this.closeAddModal}>
                    <em className="icon ni ni-cross"></em>
                  </button>
                </div>
                <div className="modal-body" style={{ overflowY: "auto", maxHeight: "65vh" }}>
                  {addErr ? <div className="alert alert-danger mb-3">{addErr}</div> : null}
                  <div className="row g-3">
                    {/* Company */}
                    <div className="col-md-6">
                      <TextField
                        required
                        fullWidth
                        variant="outlined"
                        label="Company"
                        value={form.company}
                        onChange={(e) => this.patchForm("company", e.target.value)}
                        inputProps={{ maxLength: 150, autoComplete: "off" }}
                      />
                    </div>
                    {/* Contact Name */}
                    <div className="col-md-6">
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Contact Name"
                        value={form.contact_name}
                        onChange={(e) => this.patchForm("contact_name", e.target.value)}
                        inputProps={{ maxLength: 120, autoComplete: "off" }}
                      />
                    </div>
                    {/* Email */}
                    <div className="col-md-6">
                      <TextField
                        fullWidth
                        variant="outlined"
                        type="email"
                        label="Email"
                        value={form.email}
                        onChange={(e) => this.patchForm("email", e.target.value)}
                        inputProps={{ autoComplete: "off" }}
                      />
                    </div>
                    {/* Phone */}
                    <div className="col-md-6">
                      <ReactPhoneInput
                        country="us"
                        value={(form.phone || "").replace(/^\+/, "")}
                        onChange={function(value) {
                          this.patchForm("phone", value ? "+" + value : "");
                        }.bind(this)}
                        inputProps={{ name: "phone", maxLength: 20 }}
                        specialLabel="Phone Number"
                        containerStyle={{ width: "100%" }}
                        inputStyle={{ width: "100%", height: "56px" }}
                        buttonStyle={{}}
                      />
                    </div>
                    {/* Job Title */}
                    <div className="col-md-6">
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Job Title"
                        value={form.job_title}
                        onChange={(e) => this.patchForm("job_title", e.target.value)}
                        inputProps={{ maxLength: 100, autoComplete: "off" }}
                      />
                    </div>
                    {/* Industry */}
                    <div className="col-md-6">
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Industry"
                        value={form.industry}
                        onChange={(e) => this.patchForm("industry", e.target.value)}
                        inputProps={{ maxLength: 100, autoComplete: "off" }}
                      />
                    </div>
                    {/* Deal Value (auto-calculated from selected products) */}
                    <div className="col-md-4">
                      <TextField
                        fullWidth
                        variant="outlined"
                        type="number"
                        label="Deal Value ($)"
                        value={form.deal_value}
                        disabled
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                        helperText="Auto-calculated from selected products"
                        inputProps={{ min: 0, autoComplete: "off" }}
                      />
                    </div>
                    {/* Priority */}
                    <div className="col-md-4">
                      <TextField
                        fullWidth
                        variant="outlined"
                        select
                        label="Priority"
                        value={form.priority}
                        onChange={(e) => this.patchForm("priority", e.target.value)}
                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 280 } } } }}
                      >
                        {PRIORITIES.map((p) => (
                          <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))}
                      </TextField>
                    </div>
                    {/* Stage */}
                    <div className="col-md-4">
                      <TextField
                        fullWidth
                        variant="outlined"
                        select
                        label="Stage"
                        value={form.stage_key}
                        onChange={(e) => this.patchForm("stage_key", e.target.value)}
                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 280 } } } }}
                      >
                        {stages.map((s) => (
                          <MenuItem key={s.stage_key} value={s.stage_key}>{s.label}</MenuItem>
                        ))}
                      </TextField>
                    </div>
                    {/* Source */}
                    <div className="col-md-6">
                      <TextField
                        fullWidth
                        variant="outlined"
                        select
                        label="Source"
                        value={form.source}
                        onChange={(e) => this.patchForm("source", e.target.value)}
                        SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 280 } } } }}
                      >
                        <MenuItem value="">-- Select Source --</MenuItem>
                        {LEAD_SOURCES.map((s) => (
                          <MenuItem key={s} value={s}>{s}</MenuItem>
                        ))}
                      </TextField>
                    </div>
                    {/* Next Action Date */}
                    <div className="col-md-6">
                      <TextField
                        fullWidth
                        variant="outlined"
                        type="date"
                        label="Next Action Date"
                        value={form.next_action_date}
                        onChange={(e) => this.patchForm("next_action_date", e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ autoComplete: "off" }}
                      />
                    </div>
                    {/* Partner */}
                    <div className="col-md-6">
                      <Autocomplete
                        options={partners.map((p) => ({
                          id: p.accountno,
                          label: `${p.comp_name || p.name}${p.email ? " (" + p.email + ")" : ""}`,
                        }))}
                        getOptionLabel={(option) => option.label || ""}
                        value={
                          form.partner_accountno
                            ? (() => {
                                const p = partners.find((x) => x.accountno === form.partner_accountno);
                                return p
                                  ? {
                                      id: p.accountno,
                                      label: `${p.comp_name || p.name}${p.email ? " (" + p.email + ")" : ""}`,
                                    }
                                  : null;
                              })()
                            : null
                        }
                        onChange={(e, newValue) => this.onPartnerChange(newValue?.id || "")}
                        disabled={partnersLoading}
                        clearOnBlur={false}
                        clearOnEscape={false}
                        noOptionsText="No Partners"
                        PopperComponent={BottomPopper}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Assign Partner"
                            variant="outlined"
                            fullWidth
                            placeholder={partnersLoading ? "Loading partners…" : "-- No Partner --"}
                            inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                          />
                        )}
                      />
                    </div>
                    {/* Website */}
                    <div className="col-md-6">
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Website"
                        value={form.website}
                        onChange={(e) => this.patchForm("website", e.target.value)}
                        placeholder="https://"
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ autoComplete: "off" }}
                      />
                    </div>
                    {/* Address */}
                    <div className="col-12">
                      <GooglePlacesAutocomplete
                        apiKey={process.env.REACT_APP_ADDRESS_AUTOCOMPLETE_API_KEY}
                        apiOptions={{ loading: "async" }}
                        selectProps={{
                          value: form.address ? { label: form.address, value: form.address } : null,
                          onChange: this.onAddressSelect,
                          placeholder: "Search address…",
                          isClearable: true,
                          styles: {
                            control: function(b) {
                              return Object.assign({}, b, { minHeight: 56, height: 56, fontSize: 14 });
                            },
                            valueContainer: function(b) {
                              return Object.assign({}, b, { height: 56, padding: "0 12px" });
                            },
                            input: function(provided) {
                              return Object.assign({}, provided, { color: "black", margin: 0, padding: 0 });
                            },
                            indicatorsContainer: function(b) {
                              return Object.assign({}, b, { height: 56 });
                            },
                            placeholder: function(b) {
                              return Object.assign({}, b, { color: "#8094ae" });
                            },
                            option: function(provided) {
                              return Object.assign({}, provided, { color: "black" });
                            },
                            singleValue: function(provided) {
                              return Object.assign({}, provided, { color: "black" });
                            },
                            menu: function(b) { return Object.assign({}, b, { zIndex: 9999 }); },
                          },
                        }}
                        className="googleLocation-autocomplete"
                      />
                    </div>
                    {/* Partner Products */}
                    {partnerProducts.length > 0 && (
                      <div className="col-12">
                        <label className="form-label" style={{ fontWeight: 600, fontSize: 13, color: "#526484", marginBottom: 6 }}>
                          Interested Products
                        </label>
                        {(form.products || []).length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                            {form.products.map((p, idx) => (
                              <span
                                key={`${p.service_id}-${idx}`}
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 6,
                                  padding: "4px 10px",
                                  background: "#e6f0ff",
                                  color: "#1c5bde",
                                  borderRadius: 14,
                                  fontSize: 12,
                                  fontWeight: 500,
                                }}
                              >
                                {p.service_title}
                                <span
                                  onClick={() => this.removeProductAt(idx)}
                                  style={{ cursor: "pointer", color: "#1c5bde", fontWeight: 700, lineHeight: 1 }}
                                  title="Remove"
                                >
                                  ×
                                </span>
                              </span>
                            ))}
                          </div>
                        )}
                        <Autocomplete
                          options={partnerProducts.map((svc) => ({
                            id: svc.service_id,
                            label: svc.service_title,
                            svc,
                          }))}
                          getOptionLabel={(option) => option.label || ""}
                          getOptionSelected={() => false}
                          value={null}
                          inputValue={this.state.productSearch || ""}
                          onInputChange={(e, newInput, reason) => {
                            if (reason === "input") this.setState({ productSearch: newInput });
                            else if (reason === "clear") this.setState({ productSearch: "" });
                          }}
                          onChange={(e, newValue) => {
                            if (newValue && newValue.svc) {
                              this.addProduct(newValue.svc);
                              this.setState({ productSearch: "" });
                            }
                          }}
                          disableCloseOnSelect
                          clearOnBlur={false}
                          clearOnEscape={false}
                          noOptionsText="No products"
                          PopperComponent={BottomPopper}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Search & select products…"
                              inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                            />
                          )}
                        />
                      </div>
                    )}
                    {/* Notes */}
                    <div className="col-12">
                      <TextField
                        fullWidth
                        variant="outlined"
                        label="Notes"
                        value={form.notes}
                        onChange={(e) => this.patchForm("notes", e.target.value)}
                        placeholder="Initial notes or context about this lead…"
                        multiline
                        rows={3}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ autoComplete: "off" }}
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-outline-light" onClick={this.closeAddModal} disabled={adding}>Cancel</button>
                  <button
                    type="button"
                    className="btn"
                    style={{ background: THEME_COLOR, color: THEME_TEXT_COLOR }}
                    disabled={adding || !form.company.trim()}
                    onClick={this.submitAddLead}
                  >
                    {adding ? "Saving…" : "Create Lead"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ====== Bulk Import Leads Modal ====== */}
        <BulkImportLeadsModal
          isOpen={this.state.showBulkImport}
          onClose={this.closeBulkImportModal}
          onImportSuccess={this.onBulkImportSuccess}
        />
      </div>
    );
  }
}